/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiIcon from 'components/Ui/UiIcon'
import clsx from 'clsx'
import { exportToPdfFromTable, exportToXlsxFromTable } from 'utils/exportUtils'
import { useAppDispatch } from 'helpers/store'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import ExportTagsModal from 'components/Ui/ExportTagsModal'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'

import { ArrowDownTrayIcon, ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import UiDropdown, { UiDropdownItem } from 'components/Ui/UiDropdown'
import { Table } from '@tanstack/react-table'

interface UiExportButtonSingleProps {
    onClick?: (options?: { addExportTags?: string[] }) => void | Promise<void>
    tableInit?: { table: Table<any> }
    filename?: string
    disabled?: boolean
    className?: string
    includeExportTags?: boolean
}

export function UiExportPdfButton({
    onClick,
    tableInit,
    filename,
    disabled,
    className,
    includeExportTags = false,
}: UiExportButtonSingleProps) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isExporting, setIsExporting] = React.useState(false)

    const runExport = React.useCallback(
        async (fn?: () => void | Promise<void>) => {
            setIsExporting(true)
            try {
                if (fn) {
                    await fn()
                    return
                }
                if (tableInit?.table) {
                    await exportToPdfFromTable(tableInit.table, filename || t('Export'))
                }
            } finally {
                setIsExporting(false)
            }
        },
        [filename, tableInit, t]
    )

    const handlePdfExport = () => {
        if (!includeExportTags) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Export PDF')}
                        message={t('Are you sure you want to export the current view to PDF?')}
                        onConfirm={async () => {
                            try {
                                await runExport(onClick)
                            } finally {
                                dispatch(closeSecondaryModal())
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
            return
        }

        dispatch(
            openSecondaryModal(
                <ExportTagsModal
                    title={t('Export PDF')}
                    message={t('Add export tags if you want them included in the exported file.')}
                    confirmLabel={t('Export') as string}
                    onConfirm={async (tags) => {
                        try {
                            await runExport(() => onClick?.({ addExportTags: tags }))
                        } finally {
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    return (
        <UiButton
            onClick={handlePdfExport}
            disabled={disabled || isExporting}
            loading={isExporting}
            title={t('Export PDF') as string}
            style={UiButtonStyle.Secondary}
            size={UiButtonSize.Normal}
            className={clsx(
                'UiExportPdfButton',
                className
            )}
            icon={<UiIcon name="file-text" className="w-5 h-5 text-red-500" />}
        />
    )
}

export function UiExportXlsxButton({
    onClick,
    tableInit,
    filename,
    disabled,
    className,
    includeExportTags = false,
}: UiExportButtonSingleProps) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isExporting, setIsExporting] = React.useState(false)

    const runExport = React.useCallback(
        async (fn?: () => void | Promise<void>) => {
            setIsExporting(true)
            try {
                if (fn) {
                    await fn()
                    return
                }
                if (tableInit?.table) {
                    await exportToXlsxFromTable(tableInit.table, filename || t('Export'))
                }
            } finally {
                setIsExporting(false)
            }
        },
        [filename, tableInit, t]
    )

    const handleXlsxExport = () => {
        if (!includeExportTags) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Export XLSX')}
                        message={t('Are you sure you want to export the current view to XLSX?')}
                        onConfirm={async () => {
                            try {
                                await runExport(onClick)
                            } finally {
                                dispatch(closeSecondaryModal())
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
            return
        }

        dispatch(
            openSecondaryModal(
                <ExportTagsModal
                    title={t('Export XLSX')}
                    message={t('Add export tags if you want them included in the exported file.')}
                    confirmLabel={t('Export') as string}
                    onConfirm={async (tags) => {
                        try {
                            await runExport(() => onClick?.({ addExportTags: tags }))
                        } finally {
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    return (
        <UiButton
            onClick={handleXlsxExport}
            disabled={disabled || isExporting}
            loading={isExporting}
            title={t('Export XLSX') as string}
            style={UiButtonStyle.Secondary}
            size={UiButtonSize.Normal}
            className={clsx(
                'UiExportXlsxButton',
                className
            )}
            icon={
                <UiIcon
                    name="file-spreadsheet"
                    className="w-5 h-5 text-green-600"
                />
            }
        />
    )
}

interface UiExportButtonProps {
    tableInit?: { table: Table<any> }
    filename?: string
    disabled?: boolean
    className?: string
    onPdfClick?: (options?: { addExportTags?: string[] }) => void | Promise<void>
    onXlsxClick?: (options?: { addExportTags?: string[] }) => void | Promise<void>
    triggerLabel?: string
    leadingItems?: UiDropdownItem[]
    additionalItems?: UiDropdownItem[]
    includeExportTags?: boolean
}

export default function UiExportButton({
    tableInit,
    filename,
    className,
    onPdfClick,
    onXlsxClick,
    triggerLabel,
    leadingItems,
    additionalItems,
    includeExportTags = false,
}: UiExportButtonProps) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isExporting, setIsExporting] = React.useState(false)

    const runExport = React.useCallback(
        async (fn?: () => void | Promise<void>, fallback?: () => void | Promise<void>) => {
            setIsExporting(true)
            try {
                if (fn) {
                    await fn()
                    return
                }
                if (fallback) {
                    await fallback()
                }
            } finally {
                setIsExporting(false)
            }
        },
        []
    )

    const handlePdfExport = () => {
        if (!includeExportTags) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Export PDF')}
                        message={t('Are you sure you want to export the current view to PDF?')}
                        onConfirm={async () => {
                            try {
                                await runExport(onPdfClick, () => {
                                    if (tableInit?.table) {
                                        exportToPdfFromTable(tableInit.table, filename || t('Export'))
                                    }
                                })
                            } finally {
                                dispatch(closeSecondaryModal())
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
            return
        }

        dispatch(
            openSecondaryModal(
                <ExportTagsModal
                    title={t('Export PDF')}
                    message={t('Add export tags if you want them included in the exported file.')}
                    confirmLabel={t('Export') as string}
                    onConfirm={async (tags) => {
                        try {
                            await runExport(
                                onPdfClick ? () => onPdfClick({ addExportTags: tags }) : undefined,
                                () => {
                                    if (tableInit?.table) {
                                        exportToPdfFromTable(tableInit.table, filename || t('Export'))
                                    }
                                }
                            )
                        } finally {
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const handleXlsxExport = () => {
        if (!includeExportTags) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Export XLSX')}
                        message={t('Are you sure you want to export the current view to XLSX?')}
                        onConfirm={async () => {
                            try {
                                await runExport(onXlsxClick, async () => {
                                    if (tableInit?.table) {
                                        await exportToXlsxFromTable(tableInit.table, filename || t('Export'))
                                    }
                                })
                            } finally {
                                dispatch(closeSecondaryModal())
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
            return
        }

        dispatch(
            openSecondaryModal(
                <ExportTagsModal
                    title={t('Export XLSX')}
                    message={t('Add export tags if you want them included in the exported file.')}
                    confirmLabel={t('Export') as string}
                    onConfirm={async (tags) => {
                        try {
                            await runExport(
                                onXlsxClick ? () => onXlsxClick({ addExportTags: tags }) : undefined,
                                async () => {
                                    if (tableInit?.table) {
                                        await exportToXlsxFromTable(tableInit.table, filename || t('Export'))
                                    }
                                }
                            )
                        } finally {
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const dropdownItems: UiDropdownItem[] = [
        ...(leadingItems || []),
        {
            label: t('Export PDF'),
            onClick: handlePdfExport,
            disabled: isExporting,
            icon: ({ className }) => (
                <UiIcon
                    name="file-text"
                    className={clsx('w-5 h-5 text-red-500', className)}
                    strokeWidth={1.5}
                />
            ),
        },
        {
            label: t('Export XLSX'),
            onClick: handleXlsxExport,
            disabled: isExporting,
            icon: ({ className }) => (
                <UiIcon
                    name="file-spreadsheet"
                    className={clsx('w-5 h-5 text-green-600', className)}
                    strokeWidth={1.5}
                />
            ),
        },
        ...(additionalItems || []),
    ]

    return (
        <UiDropdown
            items={dropdownItems}
            trigger={
                <div
                    className={clsx(
                        'inline-flex items-center justify-center rounded-lg border border-cta-light bg-white px-4 py-2 text-sm font-semibold text-cta-light shadow-md transition-all duration-200 hover:bg-cta-50 focus:outline-none focus:border-2 focus:border-offset-2 transform disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:text-cta-200 dark:border-cta-500 dark:hover:bg-cta-800',
                        isExporting && 'opacity-60 pointer-events-none',
                        className
                    )}
                >
                    {isExporting ? (
                        <ArrowPathIcon
                            className="mr-2 h-5 w-5 animate-spin"
                            aria-hidden="true"
                        />
                    ) : (
                        <ArrowDownTrayIcon
                            className="mr-2 h-5 w-5"
                            aria-hidden="true"
                        />
                    )}
                    {isExporting ? t('Loading...') : (triggerLabel || t('Export'))}
                    {!isExporting && (
                        <ChevronDownIcon
                            className="ml-2 h-4 w-4"
                            aria-hidden="true"
                        />
                    )}
                </div>
            }
        />
    )
}
